import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import IP_URL from '../Components/connect';
import styled, { keyframes } from 'styled-components';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Papa from 'papaparse';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Download from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import SpeedIcon from '@mui/icons-material/Speed';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import BadgeIcon from '@mui/icons-material/Badge';
import SellIcon from '@mui/icons-material/Sell';
import StraightenIcon from '@mui/icons-material/Straighten';
import Select from 'react-select';
import '../App.css';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectUser } from '../Redux/currentUserSlice';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Main = styled.div``;

const Container = styled.div`
  width: 96%;
  margin: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 100px;
`;

const Content = styled.div`
  margin-top: 20px;
`;

const Section = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 18px 24px;
  background: linear-gradient(135deg, #1976d2, #4180fd);
  border-radius: 16px;
  min-height: 120px;
  color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 14px;
  flex: 1;
  min-width: 260px;
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 10px 14px;
  backdrop-filter: blur(2px);
`;

const StatIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
`;

const StatText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.25;

  span:first-child {
    font-size: 11px;
    opacity: 0.85;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  span:last-child {
    font-size: 15px;
    font-weight: 700;
  }
`;

const RefreshRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  opacity: 0.9;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.18);
  border: none;
  color: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    font-size: 15px;
    animation: ${({ spinning }) => (spinning ? 'spin 0.8s linear infinite' : 'none')};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ChartContainer = styled.div`
  width: 100%;
`;

const Table = styled.table`
  border-collapse: collapse;
  background-color: #fdfdfd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  width: 100%;

  th, td {
    padding: 12px 15px;
    border: 1px solid #e0e0e0;
    text-align: center;
    white-space: nowrap;
  }

  th {
    background-color: #1976d2;
    color: white;
    font-weight: bold;
    cursor: pointer;
    user-select: none;
  }

  th .sort-icon {
    font-size: 13px;
    vertical-align: middle;
    margin-left: 4px;
  }

  tbody tr {
    transition: background 0.2s ease;
  }

  tbody tr:nth-child(odd) {
    background-color: #cce5f8ff;
  }

  tbody tr:hover {
    background-color: #e3f2fd;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    th, td {
      padding: 6px 8px;
    }
  }
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid #eee;
  padding: 4px 0 14px;
`;

/* Segmented pill-style filter toggle, replacing the old raw button row */
const FilterToggle = styled.div`
  display: inline-flex;
  background: #eef2f7;
  border-radius: 10px;
  padding: 4px;
  gap: 2px;
  flex-wrap: wrap;
  justify-content: center;
`;

const FilterPillButton = styled.button`
  border: none;
  background: ${(p) => (p.selected ? '#1976d2' : 'transparent')};
  color: ${(p) => (p.selected ? '#fff' : '#475569')};
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.1s ease;
  box-shadow: ${(p) => (p.selected ? '0 2px 6px rgba(25,118,210,0.35)' : 'none')};

  &:hover:not(:disabled) {
    ${(p) => !p.selected && 'background: rgba(25,118,210,0.1); color: #1976d2;'}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 600px) {
    padding: 7px 12px;
    font-size: 12px;
  }
`;

const SectionHeader = styled.h3`
  margin-top: 20px;
`;

/* Redesigned meter-info header: a title row (name) + a row of icon badges (serial, size) */
const MeterInfoHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 16px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  background: #fff;
  gap: 14px;
`;

const MeterTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MeterTitleIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(25, 118, 210, 0.12);
  color: #1976d2;
`;

const MeterTitleText = styled.h2`
  margin: 0;
  font-size: 19px;
  color: #1e293b;
  font-weight: 700;
`;

const InfoBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const InfoBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  background: #f1f5f9;
  color: #334155;
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 13px;

  svg {
    font-size: 16px;
    color: #1976d2;
  }

  b {
    color: #0f172a;
  }
`;

const MeterInfoHeaderLegacy = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 12px 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
  border-radius: 10px;
  gap: 14px;

  p {
    padding: 0;
    margin: 3px;
    font-weight: 500;
    font-size: 16px;
  }
`;

const CustomFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  z-index: 1000;
  left: 82%;
  top: 0;
  width: 220px;
  margin-top: 5px;

  @media(max-width: 600px) {
    position: relative;
    top: 0;
    left: 75%;
  }
`;

const CustomFilter = styled.div`
  font-family: 'Source Sans Pro', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: #f9f9f9;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.6);
  position: absolute;
  top: 15%;
  left: 0;
  width: 200px;
  z-index: 1000;

  label {
    display: flex;
    flex-direction: column;
    width: 100%;
    font-size: 13px;
  }

  input {
    margin-top: 2px;
    padding: 3px;
    border: 1px solid #ccc;
    border-radius: 3px;
    width: 80px;
  }

  @media (max-width:600px) {
    padding: 6px;
    top: 50%;
    width: 130px;
  }
`;

const DateWarning = styled.div`
  color: #d9534f;
  font-size: 11px;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ValveCard = styled.div`
  background: linear-gradient(145deg, #f5f7fa, #e4e7eb);
  border-radius: 16px;
  padding: 12px 16px 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  transition: all 0.3s ease;
  width: 100%;
  max-width: 380px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0,0,0,0.1);
  }
`;

const ValveCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const Title = styled.h4`
  font-size: 15px;
  color: #333;
  margin: 0;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusLED = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ open }) => (open ? "#0fdd7f" : "#ff5c5c")};
  box-shadow: ${({ open }) =>
    open
      ? "0 0 6px 2px rgba(15,221,127,0.5)"
      : "0 0 6px 2px rgba(255,92,92,0.5)"};
`;

const StatusText = styled.span`
  font-weight: 600;
  color: ${({ open }) => (open ? "#0f5132" : "#842029")};
  font-size: 14px;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: #dcdcdc;
  margin: 14px 0;
`;

// --- ANIMATION ---
const slideDown = keyframes`
  from { max-height: 0; opacity: 0; transform: translateY(-5px); }
  to { max-height: 200px; opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { max-height: 200px; opacity: 1; transform: translateY(0); }
  to { max-height: 0; opacity: 0; transform: translateY(-5px); }
`;

const ControlSection = styled.div`
  overflow: hidden;
  animation: ${({ expanded }) => (expanded ? slideDown : slideUp)} 0.4s ease forwards;
  margin-top: 10px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 10px;
`;

const ControlButton = styled.button`
  flex: 1;
  padding: 10px 14px;
  border: 1px solid ${({ variant }) =>
    variant === "open" ? "#1fa67a" : "#d9534f"};
  border-radius: 12px;
  background: ${({ active, variant }) =>
    active
      ? variant === "open"
        ? "linear-gradient(135deg, #1fa67a, #28d19a)"
        : "linear-gradient(135deg, #d9534f, #ff7676)"
      : "#f5f7fa"};
  color: ${({ active, variant }) =>
    active ? "#fff" : variant === "open" ? "#1fa67a" : "#d9534f"};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: ${({ active }) =>
    active ? "0 0 10px rgba(0,0,0,0.1)" : "none"};

  &:hover:not(:disabled) {
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// --- Confirmation Modal ---
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 22px 24px;
  width: 100%;
  max-width: 360px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  text-align: center;
`;

const ModalTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 17px;
  color: #222;
`;

const ModalText = styled.p`
  margin: 0 0 18px;
  font-size: 14px;
  color: #555;
`;

const ModalButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: scale(1.03);
  }
`;

const ConfirmButton = styled(ModalButton)`
  background: ${({ variant }) => (variant === "open" ? "#1fa67a" : "#d9534f")};
  color: #fff;
`;

const CancelButton = styled(ModalButton)`
  background: #eee;
  color: #333;
`;

// --- Loading / Error / Empty states ---
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: ${({ size }) => size || 36}px;
  height: ${({ size }) => size || 36}px;
  border: 4px solid rgba(25, 118, 210, 0.15);
  border-top-color: #1976d2;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const PageLoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 0;
  color: #555;
  font-size: 14px;
`;

const InlineLoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 0;
  color: #777;
  font-size: 13px;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fdecea;
  border: 1px solid #f5c2c0;
  color: #7c2d29;
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const RetryButton = styled.button`
  margin-left: auto;
  background: #d9534f;
  color: #fff;
  border: none;
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;

  &:hover {
    background: #c9302c;
  }
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 0;
  font-size: 13px;
  color: #444;
`;

const PageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: #fff;
  border-radius: 6px;
  width: 30px;
  height: 30px;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #f0f6ff;
  }
`;

// Custom styles for react-select
const customStyles = {
  control: (provided) => ({
    ...provided,
    width: '110px;',
    border: '2px solid #22a28f',
    boxShadow: 'none',
    cursor: 'pointer',
    margin: 0,

    '&:hover': {
      border: '2px solid blue',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#1e9e80' : '#fff',
    color: state.isSelected ? '#fff' : '#000',
    '&:hover': {
      backgroundColor: '#22a28f',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#000',
  }),
  menu: (provided) => ({
    ...provided,
    border: '1px solid #22a28f',
    borderRadius: '4px',
  }),
  menuList: (provided) => ({
    ...provided,
    padding: 0,
  }),
};

const options = [
  { value: 'pdf', label: 'PDF' },
  { value: 'csv', label: 'CSV' }
];

const ROWS_PER_PAGE = 10;

const MeterDetails = () => {
  const { imei } = useParams();
  const meterId = imei;
  const [loading, setLoading] = useState(false); // valve command in flight
  const [meterData, setMeterData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [filter, setFilter] = useState('days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { token } = useSelector(selectUser);
  const [latestMeterReading, setLatestMeterReading] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [command] = useState("valveControl");
  const [params, setParams] = useState({});
  const imeis = [parseInt(imei)];

  // Page-level state
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [readingLoading, setReadingLoading] = useState(false);
  const [readingError, setReadingError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState(null);

  // Confirmation modal for valve control
  const [confirmAction, setConfirmAction] = useState(null); // { variant: 'open' | 'close' }

  // Table sort/pagination
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);

  const hasValve =
    !!latestMeterReading &&
    latestMeterReading.valveType !== 2 &&
    latestMeterReading.prepaidFunctionEnabled === false;

  const isValveOpen = latestMeterReading?.valveControlStatus === 0;

  const user = useSelector((state) => state.user);

  // Enhanced date formatter with AM/PM support and filter-based formats
  const formatDateTime = (isoString, activeFilter = "days") => {
    if (!isoString) return "N/A";

    const date = new Date(isoString);

    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedHours = String(hours).padStart(2, "0");

    switch (activeFilter) {
      case "days":
      case "custom":
        return `${day} ${month} ${year}, ${formattedHours}:${minutes} ${ampm}`;
      case "month":
        return `${month} ${year}`;
      case "year":
        return `${year}`;
      default:
        return `${day} ${month} ${year}, ${formattedHours}:${minutes} ${ampm}`;
    }
  };

  // Valve control - actually sends the command
  const handleSendCommand = async (paramsOverride = null) => {
    const payloadParams = paramsOverride || params;

    if (!imeis || !payloadParams || !command) return;

    setLoading(true);
    try {
      const res = await fetch(`${IP_URL}/setCommands/add-command`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          imeis,
          command,
          params: payloadParams,
        }),
      });

      if (res.ok) {
        toast.success("Command queued successfully!", {
          position: "bottom-right",
          autoClose: 3000,
        });
        setParams({});
      } else {
        toast.error("Failed to send command", { position: "bottom-center" });
      }
    } catch (err) {
      toast.error("Error sending command", { position: "bottom-center" });
    } finally {
      setLoading(false);
      setConfirmAction(null);
    }
  };

  // Opens confirmation modal instead of sending immediately
  const requestValveChange = (variant) => {
    setConfirmAction({ variant });
  };

  const confirmValveChange = () => {
    if (!confirmAction) return;
    const valveControl = confirmAction.variant === "open" ? 0 : 1;
    const newParams = { valveControl };
    setParams(newParams);
    handleSendCommand(newParams);
  };

  const handleFilter = (selectedFilter) => {
    setFilter(selectedFilter);
    setCurrentPage(1);
  };

  const handleStartDateChange = (e) => setStartDate(e.target.value);
  const handleEndDateChange = (e) => setEndDate(e.target.value);

  const dateRangeInvalid =
    filter === "custom" && startDate && endDate && new Date(startDate) > new Date(endDate);

  // Fetch latest meter reading
  const fetchMeterDetails = async (cancelledRef = null) => {
    setReadingLoading(true);
    setReadingError(null);
    try {
      const response = await fetch(`${IP_URL}/currentData/device-current-data?imei=${imei}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch meter details');

      const data = await response.json();
      if (cancelledRef?.current) return;
      setLatestMeterReading(data);
      setLastUpdated(new Date());
    } catch (err) {
      if (cancelledRef?.current) return;
      console.error('Error fetching meter details', err);
      setReadingError('Unable to load the latest meter reading.');
    } finally {
      if (!cancelledRef?.current) setReadingLoading(false);
    }
  };

  useEffect(() => {
    if (!imei) {
      setPageError('IMEI is missing from the URL');
      setPageLoading(false);
      return;
    }
    const cancelledRef = { current: false };
    fetchMeterDetails(cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imei]);

  // Fetch consumption data
  useEffect(() => {
    if (dateRangeInvalid) return;

    const cancelledRef = { current: false };

    const fetchConsumption = async () => {
      setTableLoading(true);
      setTableError(null);
      let url = `${IP_URL}/currentData/saved-meter-data?imei=${imei}&filter=${filter}`;
      try {
        if (filter === "custom") {
          url += `&startDate=${startDate}&endDate=${endDate}`;
        }

        const response = await fetch(url, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) throw new Error("Network response was not ok!");

        const data = await response.json();
        if (cancelledRef.current) return;
        let normalizedData = [];

        if (filter === "days" || filter === "custom") {
          normalizedData = data.map(item => ({
            ...item,
            totalConsumption: item.totalConsumption ? item.totalConsumption / 1000 : 0,
            label: moment.utc(item.date || item.createdAt).local().format("ddd, DD MMM"),
          }));
        }

        if (filter === "month") {
          normalizedData = data.map(item => ({
            ...item,
            totalConsumption:
              item.totalConsumption && item.totalConsumption !== "No data"
                ? item.totalConsumption / 1000
                : 0,
            label: moment().month(item.month - 1).year(item.year).format("MMM YYYY"),
          }));
        }

        if (filter === "year") {
          normalizedData = data.map(item => ({
            ...item,
            totalConsumption:
              item.totalConsumption && item.totalConsumption !== "No data"
                ? item.totalConsumption / 1000
                : 0,
            label: item.year.toString(),
          }));
        }

        setTableData(normalizedData);
        setChartData(normalizedData);
        setCurrentPage(1);
      } catch (err) {
        if (cancelledRef.current) return;
        console.error('Error fetching saved data:', err);
        setTableError('Unable to load consumption data.');
        setTableData([]);
        setChartData([]);
      } finally {
        if (!cancelledRef.current) setTableLoading(false);
      }
    };

    fetchConsumption();

    return () => {
      cancelledRef.current = true;
    };
  }, [imei, filter, startDate, endDate, token, dateRangeInvalid]);

  // Fetch meter data (name/id/size)
  const fetchMeterData = async (cancelledRef = null) => {
    try {
      const response = await fetch(`${IP_URL}/currentData/meter-data-imei?meterId=${meterId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'content-type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Network response was not ok!');

      const data = await response.json();
      if (cancelledRef?.current) return true;
      setMeterData(data);
      return true;
    } catch (err) {
      console.error('Error fetching meter data', err);
      return false;
    }
  };

  // Runs fetchMeterData and drives pageLoading/pageError; used both on mount and by the Retry button
  const loadMeterData = async (cancelledRef = null) => {
    setPageLoading(true);
    setPageError(null);
    const ok = await fetchMeterData(cancelledRef);
    if (cancelledRef?.current) return;
    if (!ok) setPageError('Unable to load meter information.');
    setPageLoading(false);
  };

  useEffect(() => {
    const cancelledRef = { current: false };
    loadMeterData(cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meterId]);

  // Helper for consistent date formatting across exports & tooltips
  const formatExportDate = (item, activeFilter) => {
    if (activeFilter === "days" || activeFilter === "custom") {
      return new Date(item.date || item.createdAt).toLocaleDateString("en-US", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } else if (activeFilter === "month") {
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${monthNames[item.month - 1]} ${item.year}`;
    } else if (activeFilter === "year") {
      return `${item.year}`;
    } else {
      return new Date(item.date || item.createdAt).toLocaleString();
    }
  };

  const getFilterLabel = (activeFilter, sDate, eDate) => {
    const formatDate = (date) =>
      new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });

    switch (activeFilter) {
      case "days":
        return sDate ? `Daily (${formatDate(sDate)})` : "Daily";
      case "custom":
        if (sDate && eDate) {
          return `Custom Range (${formatDate(sDate)} to ${formatDate(eDate)})`;
        }
        return "Custom Range";
      case "month":
        return sDate
          ? `Monthly (${new Date(sDate).toLocaleDateString("en-US", { month: "long", year: "numeric" })})`
          : "Monthly";
      case "year":
        return sDate ? `Yearly (${new Date(sDate).getFullYear()})` : "Yearly";
      default:
        return "General";
    }
  };

  // NOTE: everywhere a "meter ID" used to be shown/exported, this now surfaces the
  // meter's serial number instead (meterInfo.serialNumber), per request.
  const getMeterDetailsForExport = (data, activeFilter) => {
    const meterInfo = Array.isArray(data) ? data[0] : data;
    return {
      meterName: meterInfo?.meterName || "Unknown Meter",
      meterSerial: meterInfo?.serialNumber || "Unknown_Serial",
      meterSize: meterInfo?.meterSize || "N/A",
      filterLabel: getFilterLabel(activeFilter, startDate, endDate),
    };
  };

  const handleExportCSV = () => {
    if (!tableData || tableData.length === 0) {
      toast.info("No data to export for this range.");
      return;
    }

    const { meterName, meterSerial, filterLabel } = getMeterDetailsForExport(meterData, filter);

    const csv = Papa.unparse(
      tableData.map(item => ({
        Date: formatExportDate(item, filter),
        Consumption_m3: Number(item.totalConsumption ?? 0).toFixed(3),
      }))
    );

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const cleanMeterName = meterName.replace(/[^a-z0-9]/gi, "_");
    link.setAttribute("download", `${cleanMeterName}_${meterSerial}_${filterLabel}_Report.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    if (!tableData || tableData.length === 0) {
      toast.info("No data to export for this range.");
      return;
    }

    const doc = new jsPDF();
    const { meterName, meterSerial, meterSize, filterLabel } = getMeterDetailsForExport(meterData, filter);

    try {
      const logoUrl = "/reportlogo.jpeg";
      const logo = await fetch(logoUrl)
        .then(res => res.blob())
        .then(blob => new Promise(resolve => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        }));
      doc.addImage(logo, "PNG", 120, 10, 70, 20);
    } catch (err) {
      // Logo failed to load; continue without it rather than blocking the export
    }

    doc.setFontSize(16);
    doc.setTextColor(33, 33, 33);
    doc.text("Meter Consumption Report", 10, 20);

    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.text(`Meter Name: ${meterName}`, 10, 30);
    doc.text(`Meter Serial: ${meterSerial}`, 10, 36);
    doc.text(`Meter Type: ${meterSize}`, 10, 42);
    doc.text(`Filter: ${filterLabel}`, 10, 48);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 10, 54);

    const headers = [["#", "Date", "Consumption (m³)"]];
    const rows = tableData.map((item, index) => [
      index + 1,
      formatExportDate(item, filter),
      Number(item.totalConsumption ?? 0).toFixed(3),
    ]);

    doc.autoTable({
      head: headers,
      body: rows,
      startY: 60,
      headStyles: { fillColor: [65, 128, 253], fontSize: 12, textColor: 255 },
      bodyStyles: { fontSize: 10 },
      margin: { top: 20 },
    });

    const totalConsumption = tableData.reduce((sum, item) => sum + (item.totalConsumption || 0), 0);
    doc.setFontSize(11);
    doc.text(`Total Consumption: ${totalConsumption.toFixed(3)} m³`, 10, doc.lastAutoTable.finalY + 10);

    const cleanMeterName = meterName.replace(/[^a-z0-9]/gi, "_");
    doc.save(`${cleanMeterName}_${meterSerial}_${filterLabel}_Report.pdf`);
  };

  const handleExportSelection = (selectedOption) => {
    if (selectedOption) {
      if (selectedOption.value === 'pdf') handleExportPDF();
      else if (selectedOption.value === 'csv') handleExportCSV();
    }
  };

  // Sorting
  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Rows carry their original date/month/year fields (via ...item spread in fetchConsumption),
  // so sort on those instead of the formatted label — "Fri, 04 Jul" vs "Mon, 07 Jul" doesn't
  // compare chronologically as a string.
  const getSortableDate = (item) => {
    if (item.date || item.createdAt) return new Date(item.date || item.createdAt).getTime();
    if (item.month && item.year) return new Date(item.year, item.month - 1, 1).getTime();
    if (item.year) return new Date(item.year, 0, 1).getTime();
    return 0;
  };

  const sortedTableData = useMemo(() => {
    if (!sortConfig.key) return tableData;
    const sorted = [...tableData].sort((a, b) => {
      if (sortConfig.key === 'totalConsumption') {
        return (Number(a.totalConsumption) || 0) - (Number(b.totalConsumption) || 0);
      }
      return getSortableDate(a) - getSortableDate(b);
    });
    return sortConfig.direction === 'asc' ? sorted : sorted.reverse();
  }, [tableData, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedTableData.length / ROWS_PER_PAGE));
  const paginatedTableData = sortedTableData.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  const totalConsumptionAll = tableData.reduce(
    (acc, item) => acc + (Number(item.totalConsumption) || 0),
    0
  );

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc'
      ? <ArrowUpwardIcon className="sort-icon" />
      : <ArrowDownwardIcon className="sort-icon" />;
  };

  const canView = ['admin', 'user', 'super_admin'].includes(user?.role);

  const filterButtons = [
    { key: 'days', label: 'Days' },
    { key: 'month', label: 'Month' },
    { key: 'year', label: 'Year' },
    { key: 'custom', label: 'Custom' },
  ];

  return (
    <Main>
      <Navbar />

      {canView ? (
        <Container>
          {pageLoading ? (
            <PageLoadingWrap>
              <Spinner size={44} />
              <span>Loading meter information...</span>
            </PageLoadingWrap>
          ) : pageError ? (
            <ErrorBanner>
              <WarningAmberIcon fontSize="small" />
              <span>{pageError}</span>
              <RetryButton onClick={() => loadMeterData()}>Retry</RetryButton>
            </ErrorBanner>
          ) : (
            <>
              {meterData.map((meter, index) => (
                <MeterInfoHeader key={index}>
                  <MeterTitleRow>
                    <MeterTitleIconWrap>
                      <BadgeIcon fontSize="small" />
                    </MeterTitleIconWrap>
                    <MeterTitleText>{meter.meterName || 'Unnamed Meter'}</MeterTitleText>
                  </MeterTitleRow>

                  <InfoBadgeRow>
                    <InfoBadge>
                      <SellIcon />
                      <span>Serial: <b>{meter.serialNumber || 'N/A'}</b></span>
                    </InfoBadge>
                    <InfoBadge>
                      <StraightenIcon />
                      <span>Size: <b>{meter.meterSize || 'N/A'}</b></span>
                    </InfoBadge>
                  </InfoBadgeRow>

                  {hasValve && (
                    <ValveCard>
                      <ValveCardHeader onClick={() => setExpanded(!expanded)}>
                        <Title>Valve Control</Title>
                        <StatusIndicator>
                          <StatusLED open={isValveOpen} />
                          <StatusText open={isValveOpen}>
                            {isValveOpen ? "Open" : "Closed"}
                          </StatusText>
                        </StatusIndicator>
                      </ValveCardHeader>

                      <Divider />
                      {expanded && (
                        <ControlSection expanded={expanded}>
                          <p style={{ color: "#555", fontWeight: 500, marginBottom: 8 }}>
                            Manage Valve Position
                          </p>
                          <ButtonGroup>
                            <ControlButton
                              variant="open"
                              active={isValveOpen}
                              disabled={loading}
                              aria-label="Open valve"
                              onClick={(e) => {
                                e.stopPropagation();
                                requestValveChange("open");
                              }}
                            >
                              Open
                            </ControlButton>

                            <ControlButton
                              variant="close"
                              active={!isValveOpen}
                              disabled={loading}
                              aria-label="Close valve"
                              onClick={(e) => {
                                e.stopPropagation();
                                requestValveChange("close");
                              }}
                            >
                              Close
                            </ControlButton>
                          </ButtonGroup>
                        </ControlSection>
                      )}
                    </ValveCard>
                  )}
                </MeterInfoHeader>
              ))}

              <Content>
                {readingError && (
                  <ErrorBanner>
                    <WarningAmberIcon fontSize="small" />
                    <span>{readingError}</span>
                    <RetryButton onClick={fetchMeterDetails}>Retry</RetryButton>
                  </ErrorBanner>
                )}

                <Section>
                  <StatGrid>
                    <StatCard>
                      <StatIconWrap><WaterDropIcon fontSize="small" /></StatIconWrap>
                      <StatText>
                        <span>Reading</span>
                        <span>{latestMeterReading ? `${latestMeterReading.meterReading / 1000} m³` : '--'}</span>
                      </StatText>
                    </StatCard>

                    <StatCard>
                      <StatIconWrap><SpeedIcon fontSize="small" /></StatIconWrap>
                      <StatText>
                        <span>Flowrate</span>
                        <span>{latestMeterReading ? `${latestMeterReading.realFlowRate / 1000} m³/hr` : '--'}</span>
                      </StatText>
                    </StatCard>

                    <StatCard>
                      <StatIconWrap><AccessTimeIcon fontSize="small" /></StatIconWrap>
                      <StatText>
                        <span>Time</span>
                        <span>{latestMeterReading ? formatDateTime(latestMeterReading.createdAt) : '--'}</span>
                      </StatText>
                    </StatCard>

                    {latestMeterReading?.availableWaterBalance !== null &&
                      latestMeterReading?.availableWaterBalance !== undefined && (
                      <StatCard>
                        <StatIconWrap><AccountBalanceWalletIcon fontSize="small" /></StatIconWrap>
                        <StatText>
                          <span>Water Balance</span>
                          <span>{(latestMeterReading.availableWaterBalance) / 1000} m³</span>
                        </StatText>
                      </StatCard>
                    )}
                  </StatGrid>

                  <RefreshRow>
                    {lastUpdated && <span>Updated {moment(lastUpdated).fromNow()}</span>}
                    <RefreshButton
                      onClick={fetchMeterDetails}
                      spinning={readingLoading}
                      disabled={readingLoading}
                      aria-label="Refresh latest reading"
                    >
                      <RefreshIcon /> Refresh
                    </RefreshButton>
                  </RefreshRow>
                </Section>

                <div style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.6)', padding: '5px', borderRadius: '10px', backgroundColor: 'white', marginTop: '20px' }}>
                  <h3>Water Consumption</h3>
                  <FilterContainer>
                    <FilterToggle>
                      {filterButtons.map((f) => (
                        <FilterPillButton
                          key={f.key}
                          onClick={() => handleFilter(f.key)}
                          selected={filter === f.key}
                        >
                          {f.label}
                        </FilterPillButton>
                      ))}
                    </FilterToggle>
                  </FilterContainer>
                  <CustomFilterContainer>
                    {filter === 'custom' && (
                      <CustomFilter>
                        <label>
                          Start Date:
                          <input type="date" value={startDate} onChange={handleStartDateChange} />
                        </label>
                        <label>
                          End Date:
                          <input type="date" value={endDate} onChange={handleEndDateChange} />
                        </label>
                        {dateRangeInvalid && (
                          <DateWarning>
                            <WarningAmberIcon style={{ fontSize: 14 }} />
                            Start date must be before end date
                          </DateWarning>
                        )}
                      </CustomFilter>
                    )}
                  </CustomFilterContainer>

                  {tableError ? (
                    <ErrorBanner>
                      <WarningAmberIcon fontSize="small" />
                      <span>{tableError}</span>
                    </ErrorBanner>
                  ) : tableLoading ? (
                    <InlineLoadingWrap>
                      <Spinner size={26} />
                      <span>Loading consumption data...</span>
                    </InlineLoadingWrap>
                  ) : (
                    <ChartContainer style={{ color: '#333', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {chartData.length === 0 ? (
                        <div style={{ padding: '30px 0', color: '#888', fontSize: 14 }}>No data available for this range.</div>
                      ) : (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="label"
                              tick={{ fontSize: 12 }}
                              angle={-30}
                              textAnchor="end"
                              height={60}
                            />
                            <YAxis
                              label={{
                                value: "Consumption (m³)",
                                angle: -90,
                                position: "insideLeft"
                              }}
                            />
                            <Tooltip
                              formatter={(value) => [`${Number(value).toFixed(3)} m³`, "Consumption"]}
                              labelFormatter={(label, payload) => {
                                if (!payload || payload.length === 0) return "";
                                const dataPoint = payload[0]?.payload;
                                if (!dataPoint) return label;

                                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

                                if (dataPoint.date) {
                                  const dateObj = new Date(dataPoint.date);
                                  return `Date: ${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
                                }
                                if (dataPoint.month && dataPoint.year) {
                                  const monthStr = monthNames[dataPoint.month - 1];
                                  return `Date: ${monthStr}-${dataPoint.year}`;
                                }
                                if (dataPoint.year) return `Date: ${dataPoint.year}`;
                                return label;
                              }}
                              contentStyle={{
                                backgroundColor: "#f9f9f9",
                                borderRadius: "8px",
                                border: "1px solid #ccc",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.2)"
                              }}
                            />
                            <Bar dataKey="totalConsumption" fill="#4f46e5" radius={[8, 8, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </ChartContainer>
                  )}
                </div>
              </Content>

              <div style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.6)', padding: '5px', borderRadius: '10px', backgroundColor: 'white', marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                  <h4>Water Readings</h4>
                  <Select
                    options={options}
                    onChange={handleExportSelection}
                    placeholder={<span><Download style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />Export</span>}
                    styles={customStyles}
                    isDisabled={tableData.length === 0}
                  />
                </div>
                <Table>
                  <thead>
                    <tr>
                      <th onClick={() => requestSort('label')}>{filter} {sortIcon('label')}</th>
                      <th onClick={() => requestSort('totalConsumption')}>Consumption (m³) {sortIcon('totalConsumption')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableLoading ? (
                      <tr>
                        <td colSpan="2"><Spinner size={20} /></td>
                      </tr>
                    ) : paginatedTableData.length > 0 ? (
                      paginatedTableData.map((item, index) => (
                        <tr key={index}>
                          <td>{item.label}</td>
                          <td>{Number(item.totalConsumption ?? 0).toFixed(3)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2"><b>No Data</b></td>
                      </tr>
                    )}

                    {tableData.length > 0 && (
                      <tr>
                        <td><b>Total</b></td>
                        <td><b>{totalConsumptionAll.toFixed(3)}</b></td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                {sortedTableData.length > ROWS_PER_PAGE && (
                  <PaginationRow>
                    <PageButton
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </PageButton>
                    <span>Page {currentPage} of {totalPages}</span>
                    <PageButton
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                    >
                      <ChevronRightIcon fontSize="small" />
                    </PageButton>
                  </PaginationRow>
                )}
              </div>
            </>
          )}
        </Container>
      ) : (
        <p>You do not have the required permissions to view this content.</p>
      )}

      {confirmAction && (
        <ModalOverlay onClick={() => !loading && setConfirmAction(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {confirmAction.variant === 'open' ? 'Open the valve?' : 'Close the valve?'}
            </ModalTitle>
            <ModalText>
              This will {confirmAction.variant === 'open' ? 'open' : 'close'} the valve for this meter.
              {confirmAction.variant === 'close' && ' Water supply will be cut off.'}
            </ModalText>
            <ModalButtonRow>
              <CancelButton onClick={() => setConfirmAction(null)} disabled={loading}>
                Cancel
              </CancelButton>
              <ConfirmButton
                variant={confirmAction.variant}
                onClick={confirmValveChange}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Confirm'}
              </ConfirmButton>
            </ModalButtonRow>
          </ModalCard>
        </ModalOverlay>
      )}

      <Footer />
    </Main>
  );
};

export default MeterDetails;